/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import Fuse from 'fuse.js';

import { RootState } from '../../../app/store';
import pearlApi from '../api/pearlApi';
import { snackNotifications } from '../notification/SnackBarUtils';
import { sort_unique } from '../utils';
import { DocVersionType } from '../algorithm/types';

/** Holds a mix of document titles, and document triggers.
 *  Slug (a unique id for the document) will be null when isTitle is false
 */
export type SearchItemType =
  | { name: string; isTitle: true; flags?: string[]; slug: string }
  | { name: string; isTitle: false; flags?: string[]; slug: null };
export type SuggestionType = {
  id: string;
  title: string;
  slug: string;
  doc_type: DocVersionType;
  has_guides: boolean;
  has_algorithm: boolean;
};
export interface SearchState {
  titlesAndTriggers: SearchItemType[] | null | undefined; // null until loaded, undefined if failed to load
  dynamicSuggestions: {
    recommendedTopics?: SuggestionType[];
    viewedTopics?: SuggestionType[];
    specialities?: any;
  };
}

/** Format that the /autocomplete endpoint returns data in */
interface PearlApiAutocompleteResponse {
  titles_with_flags?: { title: string; flags: string[]; slug: string }[];
  triggers?: string[];
}

const initialState: SearchState = {
  titlesAndTriggers: undefined,
  dynamicSuggestions: {},
};

export let slugToName: Map<string, string> | undefined = undefined;
export let nameToSlug: Map<string, string> | undefined = undefined;

/**
 * This isn't the greatest pattern, and probably not safe with multiple libraries.
 * The idea is that we save the slugToName and nameToSlug maps from the autocomplete data.
 * So that when we load a document, we know the name of the document from the slug and can update the links if the document name has changed.
 */
export function updateSlugToName(titlesAndTriggers: PearlApiAutocompleteResponse) {
  slugToName = new Map(titlesAndTriggers?.titles_with_flags?.map((x) => [x.slug.trim(), x.title.trim()]) || []);
  nameToSlug = new Map(titlesAndTriggers?.titles_with_flags?.map((x) => [x.title.trim(), x.slug.trim()]) || []);

  // Likewise with @see addSlugToName, remove any double spaces from the name and add that as well.
  titlesAndTriggers?.titles_with_flags?.forEach((x) => {
    const nameWithoutDoubleSpace = x.title.replace(/\s\s+/g, ' ').trim();
    if (nameWithoutDoubleSpace !== x.title) {
      nameToSlug!.set(nameWithoutDoubleSpace, x.slug);
    }
  });
}

export function addSlugToName(slug: string, name: string) {
  if (!slugToName) slugToName = new Map();
  if (!nameToSlug) nameToSlug = new Map();
  slugToName.set(slug, name);
  nameToSlug.set(name, slug);
  // There's a very strange bug where a title can contain a double space, but the backend will ignore the double space and so match, but
  // we don't get the slug. To work around, we remove the double space from the name and add that as well.
  const nameWithoutDoubleSpace = name.replace(/\s\s+/g, ' ').trim();
  if (nameWithoutDoubleSpace !== name) {
    nameToSlug.set(nameWithoutDoubleSpace, slug);
  }
}

/** Called by the App at the start.
 *  Use selectAutocompleteIsLoading to check if the data is loaded.
 */
export const fetchAutoCompleteThunk = createAsyncThunk<
  PearlApiAutocompleteResponse, // Return type
  { publishedOnly: boolean }, // Argument type
  { state: RootState }
>('votes/fetchAutoCompleteThunk', async ({ publishedOnly }, thunkAPI) => {
  try {
    const titlesAndTriggers: PearlApiAutocompleteResponse = (
      await pearlApi.get(
        `/documentVersions/autocomplete?${publishedOnly ? 'onlyPublished=true&' : ''}return=titles_with_flags,triggers`
      )
    ).data.data;

    updateSlugToName(titlesAndTriggers);

    return titlesAndTriggers;
  } catch (error) {
    snackNotifications.error(`Failed to fetch autocomplete list: ${(error as Error).message}`);
    throw error;
  }
});

// Use one thunk and ensure all the promises are fulfilled.

export const fetchAllDynamicSuggestionsThunk = createAsyncThunk<{
  recommendations: SuggestionType[] | null;
  searchHistory: SuggestionType[] | null;
  specialities: String[] | null;
}>('search/fetchAllDynamicSuggestionsThunk', async () => {
  const requests = [
    pearlApi.get('/landing/topics/recommended').catch((error) => {
      snackNotifications.error('Failed to fetch recommendations.');
      console.error(`Failed to fetch recommendations: ${(error as Error).message}`);
      return null;
    }),
    pearlApi.get('/landing/topics/viewed').catch((error) => {
      snackNotifications.error('Unable to fetch user history');
      console.error(`Unable to fetch user history: ${(error as Error).message}`);
      return null;
    }),
    pearlApi.get('/landing/specialties').catch((error) => {
      snackNotifications.error('Unable to fetch specialities');
      console.error(`Unable to fetch specialities: ${(error as Error).message}`);
      return null;
    }),
  ];

  const results = await Promise.allSettled(requests);

  const recommendations =
    results[0]!.status === 'fulfilled' && (results[0] as any)!.value?.data ? (results[0] as any)!.value.data : null;
  const searchHistory =
    results[1]!.status === 'fulfilled' && (results[1] as any)!.value?.data ? (results[1] as any)!.value.data : null;
  const specialities =
    results[2]!.status === 'fulfilled' && (results[2] as any).value?.data
      ? (results[2] as any).value.data.data.map((specialty: any) => ({ specialty }))
      : null;
  return { recommendations, searchHistory, specialities };
});

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAutoCompleteThunk.pending, (state, data) => {
        state.titlesAndTriggers ||= null; // null until loaded, undefined if failed to load
      })
      .addCase(fetchAutoCompleteThunk.fulfilled, (state, data) => {
        const { titles_with_flags, triggers: triggers_ } = data.payload;
        // Make a new set from title_with_flags.title
        const titlesSet = new Set(titles_with_flags?.map((title) => title.title));
        // Remove any titles that are also in triggers, from triggers
        const triggers = triggers_?.filter((trigger) => trigger && !titlesSet.has(trigger)) || [];

        state.titlesAndTriggers =
          titles_with_flags
            ?.filter((info) => !!info.title)
            .map((info) => ({ name: info.title, isTitle: true as const, flags: info.flags || [], slug: info.slug })) ||
          [];
        state.titlesAndTriggers.push(
          ...triggers.map((trigger) => ({ name: trigger, isTitle: false as const, slug: null }))
        ); // A trigger can map to multiple documents, so no slug
      })
      .addCase(fetchAutoCompleteThunk.rejected, (state) => {
        state.titlesAndTriggers = undefined;
      })
      .addCase(fetchAllDynamicSuggestionsThunk.fulfilled, (state, data) => {
        state.dynamicSuggestions.recommendedTopics = data.payload.recommendations ?? undefined;
        state.dynamicSuggestions.viewedTopics = data.payload.searchHistory ?? undefined;
        state.dynamicSuggestions.specialities = data.payload.specialities ?? undefined;
      });
  },
});
export const selectDynamicSuggestionsForUser = (state: RootState) => state.search.dynamicSuggestions;
export const selectAutocomplete = (state: RootState) => {
  return state.search.titlesAndTriggers;
};
export const selectAutocompleteIsLoading = (state: RootState) => state.search.titlesAndTriggers === null;

export const selectDocumentTitles = createSelector([selectAutocomplete], (titlesAndTriggers) =>
  titlesAndTriggers ? sort_unique(titlesAndTriggers.filter((t) => t.isTitle).map((t) => '#' + t.name)) : []
);

/** For fuzzy search autocomplete */
export const selectFuseTagAutocomplete = createSelector([selectAutocomplete], (listOfTags) => {
  const options = {
    // isCaseSensitive: false,
    // includeScore: false,
    includeScore: true,
    // shouldSort: true,
    // includeMatches: false,
    // findAllMatches: false,
    // minMatchCharLength: 1,
    // location: 0,
    // threshold: 0.6,
    threshold: 0.3,
    // distance: 100,
    // useExtendedSearch: false,
    // ignoreLocation: false,
    ignoreLocation: true,
    // ignoreFieldNorm: false,
    ignoreFieldNorm: true, // Don't weight a short field higher than a long one
    // fieldNormWeight: 1,
    getFn: (obj: NonNullable<SearchState['titlesAndTriggers']>[0], path: string | string[]) =>
      (typeof path === 'string' ? path : path[0] === 'title') === obj.isTitle ? obj.name : '',
    keys: [
      { name: 'tag', weight: 0.7 },
      { name: 'title', weight: 1.0 },
    ],
  };

  return new Fuse(listOfTags || [], options);
});

export default searchSlice.reducer;
